import React from "react";
import { Link } from "react-router-dom";
import { Users, Building2, Shield, FileText, Briefcase, ClipboardList, MessageSquare, CheckCircle2, ArrowRight } from "lucide-react";
import { DashboardShell } from "../../components/shared/DashboardShell";
import { StatCard } from "../../components/shared/StatCard";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { adminNav } from "../../components/shared/navConfig";
import { adminStats, submittedTasks, candidates } from "../../data/mockData";

export default function AdminDashboard() {
  return (
    <DashboardShell nav={adminNav} title="Operations overview" subtitle="Platform-wide health and pending queues">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-10">
        <StatCard testId="admin-stat-students"     label="Students"       value={adminStats.students.toLocaleString()}     delta="+42"  icon={Users} accent/>
        <StatCard testId="admin-stat-companies"    label="Companies"      value={adminStats.companies}                     delta="+7"   icon={Building2}/>
        <StatCard testId="admin-stat-pending-kyc"  label="Pending KYC"    value={adminStats.pendingKyc}                    delta="-3"   icon={Shield}/>
        <StatCard testId="admin-stat-pending-gst"  label="Pending GST"    value={adminStats.pendingGst}                    delta="-1"   icon={FileText}/>
        <StatCard testId="admin-stat-pending-tasks"label="Pending tasks"  value={adminStats.pendingTasks}                  delta="+2"   icon={Briefcase} accent/>
        <StatCard testId="admin-stat-applications" label="Applications"   value={adminStats.applications}                  delta="+38"  icon={ClipboardList}/>
        <StatCard testId="admin-stat-allocations"  label="Allocations"    value={adminStats.allocations}                   delta="+9"   icon={MessageSquare}/>
        <StatCard testId="admin-stat-active"       label="Active tasks"   value={adminStats.activeTasks}                   delta="+4"   icon={Briefcase}/>
        <StatCard testId="admin-stat-completed"    label="Completed"      value={adminStats.completedTasks.toLocaleString()} delta="+22" icon={CheckCircle2}/>
        <StatCard testId="admin-stat-revenue"      label="GMV (30d)"      value="₹42.6L" delta="+16%" icon={Briefcase} accent/>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Pending tasks approvals */}
        <div className="lg:col-span-2 card-surface p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="eyebrow">Task approval queue</div>
              <h3 className="font-display text-lg text-white mt-1">Awaiting your review</h3>
            </div>
            <Link to="/admin/tasks" data-testid="admin-view-tasks-link" className="text-xs text-blue-400 hover:text-blue-300 inline-flex items-center gap-1">Open queue <ArrowRight size={12}/></Link>
          </div>
          <table className="hidden md:table w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-zinc-500 text-left">
                <th className="pb-3 font-normal">Company / Task</th>
                <th className="pb-3 font-normal">Location</th>
                <th className="pb-3 font-normal">Budget</th>
                <th className="pb-3 font-normal">Deadline</th>
                <th className="pb-3 font-normal text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {submittedTasks.map(t => (
                <tr key={t.id} data-testid={`admin-task-row-${t.id}`} className="border-t transition-colors duration-200 hover:bg-white/[0.02]" style={{ borderColor: "var(--border)" }}>
                  <td className="py-3">
                    <div className="text-white text-sm">{t.title}</div>
                    <div className="text-[11px] text-zinc-500">{t.company} · <span className="font-mono">{t.id}</span></div>
                  </td>
                  <td className="py-3 text-zinc-400 text-xs">{t.location}</td>
                  <td className="py-3 text-white font-mono text-xs">₹{t.budget.toLocaleString()}</td>
                  <td className="py-3 text-zinc-400 text-xs">{t.deadline}</td>
                  <td className="py-3 text-right"><StatusBadge status={t.status}/></td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Mobile cards */}
          <div className="md:hidden space-y-2">
            {submittedTasks.map(t => (
              <div key={t.id} className="p-3 rounded-md border" data-testid={`admin-task-card-${t.id}`}
                   style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div className="min-w-0">
                    <div className="text-sm truncate" style={{ color: "var(--text)" }}>{t.title}</div>
                    <div className="text-[10px]" style={{ color: "var(--text-dim)" }}>{t.company} · <span className="font-mono">{t.id}</span></div>
                  </div>
                  <StatusBadge status={t.status}/>
                </div>
                <div className="grid grid-cols-3 gap-2 text-[11px]" style={{ color: "var(--text-muted)" }}>
                  <div><div className="text-[9px] uppercase" style={{ color: "var(--text-dim)" }}>Loc</div>{t.location}</div>
                  <div><div className="text-[9px] uppercase" style={{ color: "var(--text-dim)" }}>Budget</div><span className="font-mono" style={{ color: "var(--text)" }}>₹{t.budget.toLocaleString()}</span></div>
                  <div><div className="text-[9px] uppercase" style={{ color: "var(--text-dim)" }}>Deadline</div>{t.deadline}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Allocation preview */}
        <div className="card-surface p-5">
          <div className="eyebrow">Ready for allocation</div>
          <h3 className="font-display text-lg text-white mt-1 mb-4">T-1042 · GST Filing</h3>
          <div className="space-y-3">
            {candidates.map(c => (
              <div key={c.id} className="p-3 rounded-md border" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
                <div className="flex items-center gap-3">
                  <img alt={c.name} src={c.avatar} className="w-8 h-8 rounded-full object-cover"/>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-white truncate">{c.name}</div>
                    <div className="text-[11px] text-zinc-500 truncate">{c.distance} · {c.experience}</div>
                  </div>
                  <div className={`text-xs font-mono ${c.matchScore >= 90 ? "text-emerald-400" : "text-blue-400"}`}>{c.matchScore}%</div>
                </div>
              </div>
            ))}
          </div>
          <Link to="/admin/allocation" data-testid="admin-open-allocation-btn" className="btn-primary w-full py-2 mt-5 rounded-md text-xs inline-flex items-center justify-center gap-2">
            Compare & allocate <ArrowRight size={12}/>
          </Link>
        </div>
      </div>
    </DashboardShell>
  );
}
